import { Divider, Drawer, Stack } from '@mui/material'
import { ContentChip, DisplayUserLink, InfoTooltip, tokens, User, type ExternalParticipantDisplayData } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { IconButton, TinyButton } from 'ComponentLibrary/src/piazza/buttons'
import { ArrowUpRightIcon, BellIcon, EyeIcon, XIcon } from '@phosphor-icons/react'
import { TextButton } from 'ComponentLibrary/src/piazza/buttons/TextButton/TextButton'
import { isEmpty } from 'lodash'
import { ParticipantsAutocomplete } from 'ComponentLibrary/src/widgets/Participants/ParticipantsAutocomplete'
import { Plus } from 'react-feather'
import { useState } from 'react'
import LoadingIndicator from 'ComponentLibrary/src/atoms/LoadingIndicator/LoadingIndicator'
import { ID, UUID } from 'ComponentLibrary/src/common/uuid'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type ParticipantsDrawerProps = {
    users: User[]
    participants: User[]
    currentUser?: User
    isEditParticipants: boolean
    setEditParticipants: (value: boolean) => void
    persistParticipants?: (newIds: number[]) => void
    persistObservers?: (newUUIds: UUID[]) => void
    isPersistingParticipants?: boolean
    emptyParticipantsMessage?: string
    emptyObserversMessage?: string
    canEditOtherRoles?: boolean
    disabled?: boolean
    observerUsers?: User[]
    externalParticipantUsers?: ExternalParticipantDisplayData[]
    addObserver?: (uuid: UUID) => void
    orderAssignedCustomerUsers?: ID[]
    orderAssignedAppraiserId?: string
    navigateToObserversPage?: () => void
    copy?: Partial<ParticipantsDrawerCopy>
    userLinkCopy?: Partial<UserLinkCopy>
}

export type ParticipantsDrawerCopy = {
    title: string
    addParticipantsLabel: string
    notifyMe: string
    participantCount: (count: number) => string
    observerCount: (count: number) => string
    editObservers: string
    observerInfo: string
    responsible: string
}

const DEFAULT_PARTICIPANTS_DRAWER_COPY: ParticipantsDrawerCopy = {
    title: 'Benachrichtigte bearbeiten',
    addParticipantsLabel: 'Weitere Benachrichtigte hinzufügen...',
    notifyMe: 'Mich benachrichtigen',
    participantCount: (count) => `${count} Benachrichtigte`,
    observerCount: (count) => `${count} Beobachter des Auftrags`,
    editObservers: 'Beobachter bearbeiten',
    observerInfo:
        'Beobachter werden per Mail über alle Neuigkeiten zu einem Auftrag informiert, die sie in ihren Benachrichtigungseinstellungen aktiviert haben. Dazu zählen u.A.: Änderungen des Auftragsstatus, verfügbare Entwürfe, neue Nachrichten und Aufgabenupdates. Bearbeiten Sie Ihren Status als Beobachter auf der Auftragsseite.',
    responsible: 'Zuständig',
}

export const ParticipantsDrawer = ({
    users,
    participants,
    currentUser,
    isEditParticipants,
    setEditParticipants,
    persistParticipants,
    isPersistingParticipants,
    emptyParticipantsMessage,
    emptyObserversMessage,
    canEditOtherRoles = true,
    disabled,
    observerUsers = [],
    externalParticipantUsers = [],
    orderAssignedCustomerUsers,
    orderAssignedAppraiserId,
    navigateToObserversPage,
    copy,
    userLinkCopy,
}: ParticipantsDrawerProps) => {
    const resolvedCopy = { ...DEFAULT_PARTICIPANTS_DRAWER_COPY, ...copy }
    const [newParticipants, setNewParticipants] = useState<User[]>([])

    const addNewParticipants = (newParticipantIds: number[]) =>
        persistParticipants?.([...participants.map(({ id }) => id), ...newParticipantIds])

    const removeParticipant = (participantIdToRemove: number) =>
        persistParticipants?.(participants.filter((p) => p.id !== participantIdToRemove).map(({ id }) => id))

    const removeDisplayedParticipant = (participant: User) => {
        removeParticipant(participant.id)
    }

    const handleAddParticipantsButtonClick = () => {
        addNewParticipants(newParticipants.map(({ id }) => id))
        setNewParticipants([])
    }

    return (
        <Drawer open={isEditParticipants} onClose={() => setEditParticipants(false)} anchor="right" sx={{ zIndex: 1550 }}>
            <Stack direction="column" width="580px">
                <Stack direction="row" padding={tokens.SizingSpacing300} justifyContent="space-between" alignItems="center">
                    <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                        <Typography variant="headline1" color={tokens.ColorTextBoldest}>
                            {resolvedCopy.title}
                        </Typography>
                        <LoadingIndicator isPending={!!isPersistingParticipants} />
                    </Stack>
                    <IconButton
                        variant="tertiary"
                        size="medium"
                        icon={<XIcon size={tokens.SizingIconM} color={tokens.ColorIconBoldest} />}
                        onClick={() => setEditParticipants(false)}
                    />
                </Stack>
                <Divider />
                <Stack direction="column" padding={tokens.SizingSpacing300} gap={tokens.SizingSpacing400}>
                    <>
                        <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                            <ParticipantsAutocomplete
                                users={users.filter((user) => !participants.find((participant) => participant.id === user.id))}
                                newParticipants={newParticipants}
                                setNewParticipants={setNewParticipants}
                                disabled={disabled}
                                label={resolvedCopy.addParticipantsLabel}
                                userLinkCopy={userLinkCopy}
                            />
                            <IconButton
                                size="medium"
                                icon={<Plus />}
                                onClick={handleAddParticipantsButtonClick}
                                disabled={isEmpty(newParticipants)}
                            />
                        </Stack>

                        {currentUser && !disabled && !participants.find((participant) => participant.id === currentUser.id) && (
                            <Stack direction="row" gap={tokens.SizingSpacing100} justifyContent="space-between">
                                <DisplayUserLink size="medium" user={currentUser} disablePortal copy={userLinkCopy} />
                                <TextButton
                                    icon={<BellIcon />}
                                    label={resolvedCopy.notifyMe}
                                    onClick={() => addNewParticipants([currentUser.id])}
                                />
                            </Stack>
                        )}
                        <Stack direction="column" gap={tokens.SizingSpacing150}>
                            <Typography variant="textM" color={tokens.ColorTextDefault}>
                                {resolvedCopy.participantCount(participants.length)}
                            </Typography>
                            {isEmpty(participants) && (
                                <Typography variant="textS" color={tokens.ColorTextDefault}>
                                    {emptyParticipantsMessage}
                                </Typography>
                            )}
                            {participants.map((participant) => (
                                <Stack
                                    direction="row"
                                    key={participant.uuid}
                                    gap={tokens.SizingSpacing150}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <DisplayUserLink user={participant} size="medium" disablePortal copy={userLinkCopy} />
                                    <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                                        {(currentUser?.role === participant.role || canEditOtherRoles) && !disabled && (
                                            <IconButton
                                                size="small"
                                                variant="tertiary"
                                                icon={<XIcon color={tokens.ColorIconDefault} weight="bold" />}
                                                onClick={() => removeDisplayedParticipant(participant)}
                                                disabled={isPersistingParticipants}
                                            />
                                        )}
                                    </Stack>
                                </Stack>
                            ))}
                        </Stack>
                    </>
                    <Stack direction="column" gap={tokens.SizingSpacing150}>
                        <Stack direction="row" justifyContent={'space-between'} alignItems={'center'}>
                            <Typography variant="textM" color={tokens.ColorTextDefault}>
                                {resolvedCopy.observerCount(observerUsers.length + externalParticipantUsers.length)}
                            </Typography>
                            <Stack direction="row" alignItems={'center'} spacing={tokens.SizingSpacing150}>
                                {navigateToObserversPage && (
                                    <TinyButton
                                        leftIcon={<EyeIcon />}
                                        label={resolvedCopy.editObservers}
                                        rightIcon={<ArrowUpRightIcon />}
                                        onClick={navigateToObserversPage}
                                    />
                                )}
                                <InfoTooltip
                                    text={resolvedCopy.observerInfo}
                                    size={tokens.SizingIconS}
                                    style={{ color: tokens.ColorIconSubtle }}
                                />
                            </Stack>
                        </Stack>
                        {isEmpty(observerUsers) && isEmpty(externalParticipantUsers) && (
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {emptyObserversMessage}
                            </Typography>
                        )}
                        {observerUsers.map((observer) => {
                            const responsibleForOrder =
                                orderAssignedCustomerUsers?.includes(observer.id) || observer.uuid === orderAssignedAppraiserId
                            return (
                                <Stack
                                    direction="row"
                                    key={observer.uuid}
                                    gap={tokens.SizingSpacing150}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <DisplayUserLink user={observer} size="medium" disablePortal copy={userLinkCopy} />
                                    <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                                        {currentUser?.id && responsibleForOrder && (
                                            <ContentChip size="small" color="royal" label={resolvedCopy.responsible} />
                                        )}
                                    </Stack>
                                </Stack>
                            )
                        })}
                        {externalParticipantUsers.map((observer) => {
                            const responsibleForOrder = observer.uuid === orderAssignedAppraiserId
                            return (
                                <Stack
                                    direction="row"
                                    key={observer.uuid}
                                    gap={tokens.SizingSpacing150}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <DisplayUserLink user={observer} size="medium" disablePortal isExternal copy={userLinkCopy} />
                                    <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                                        {currentUser?.id && responsibleForOrder && (
                                            <ContentChip size="small" color="royal" label={resolvedCopy.responsible} />
                                        )}
                                    </Stack>
                                </Stack>
                            )
                        })}
                    </Stack>
                </Stack>
            </Stack>
        </Drawer>
    )
}
